import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { concatMap, from, tap } from 'rxjs';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../../auth/services/auth.service';
import { StopUserImpersonation } from '../../states/user.actions';
import { ImpersonationDialogComponent } from '../impersonation/impersonation-dialog/impersonation-dialog.component';
import { SelectImpersonationDialogComponent } from '../impersonation/select-impersonation-dialog/select-impersonation-dialog.component';
import { SidebarService, SidebarTab } from '../../services/sidebar.service';
import { ApplicationFilterDialogComponent } from './dialogs/application-filter-dialog/application-filter-dialog.component';
import { TimesheetSettingsDialogComponent } from './dialogs/timesheet-settings-dialog/timesheet-settings-dialog.component';
import { CreateNewsDialogComponent } from './dialogs/create-news-dialog/create-news-dialog.component';
import { DeepLinkDialog, DIALOG_QUERY_PARAM } from '../../enums/DeepLinkDialog.enum';

@Injectable({
  providedIn: 'root',
})
export class HeaderOperationsService {
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private router = inject(Router);
  private authService = inject(AuthService);
  private sidebarService = inject(SidebarService);

  public changeLang(langCode: string): void {
    // Clean the language code to remove any quotes
    const cleanLangCode = langCode.replace(/^["']|["']$/g, '');
    this.translate.use(cleanLangCode);
    document.documentElement.lang = cleanLangCode;
    localStorage.setItem('language', cleanLangCode);
  }

  public setTheme(mode: 'dark' | 'light'): void {
    this.themeService.setTheme(mode);
    localStorage.setItem('theme', JSON.stringify(mode));
  }

  public openApplicationFilterDialog(): void {
    this.dialog.open(ApplicationFilterDialogComponent, {
      panelClass: 'difa-dialog',
    });
    this.sidebarService.close();
  }

  public openTimesheetSettingsDialog(): void {
    this.dialog.open(TimesheetSettingsDialogComponent, {
      panelClass: 'difa-dialog',
    });
    this.sidebarService.close();
  }

  public openCreateNewsDialog(): void {
    this.dialog.open(CreateNewsDialogComponent, {
      panelClass: 'difa-dialog',
    });
    this.sidebarService.close();
  }

  public openEditImpersonationDialog(): void {
    this.dialog.open(ImpersonationDialogComponent, {
      panelClass: 'difa-dialog',
    });
    this.sidebarService.close();
  }

  public openSelectImpersonationDialog(): void {
    this.dialog.open(SelectImpersonationDialogComponent, {
      panelClass: 'difa-dialog',
    });
    this.sidebarService.close();
  }

  /** Opens the dialog requested by a deep link (`?dialog=impersonation`) and removes the parameter. */
  public openDialogFromUrl(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const { [DIALOG_QUERY_PARAM]: dialog, ...queryParams } = urlTree.queryParams;
    if (dialog !== DeepLinkDialog.IMPERSONATION) {
      return;
    }

    urlTree.queryParams = queryParams;
    void this.router.navigateByUrl(urlTree, { replaceUrl: true });
    this.openEditImpersonationDialog();
  }

  public stopImpersonation(): void {
    const dispatchAction$ = this.store.dispatch(new StopUserImpersonation());
    dispatchAction$.pipe(concatMap(() => from(this.router.navigate(['/home'])).pipe(tap(() => window.location.reload())))).subscribe();
  }

  public navigateToPersonalInformationPage(): void {
    void this.router.navigate(['/personal-information']);
    this.sidebarService.close();
  }

  public navigateToReleaseNotesPage(): void {
    void this.router.navigate(['/release-notes']);
    this.sidebarService.close();
  }

  public isImpersonating(): boolean {
    return this.authService.impersonatedUser() != null;
  }

  public getCurrentTheme(): 'dark' | 'light' {
    const theme = localStorage.getItem('theme') || '"light"';
    return JSON.parse(theme) as 'dark' | 'light';
  }

  public getCurrentLanguage(): string {
    const language = localStorage.getItem('language') || 'en';
    // Remove quotes if they exist (fix for double-encoded language codes)
    return language.replace(/^["']|["']$/g, '');
  }

  public toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  public openSidebar(tab: SidebarTab): void {
    this.sidebarService.open(tab);
  }

  public closeSidebar(): void {
    this.sidebarService.close();
  }

  public setIsMobile(isMobile: boolean): void {
    this.sidebarService.setIsMobile(isMobile);
  }
}
